import { AggregationContainer } from '@opensearch-project/opensearch/api/_types/_common.aggregations';
import {
  MatchQuery,
  QueryContainer,
} from '@opensearch-project/opensearch/api/_types/_common.query_dsl';
import { Sort } from '@opensearch-project/opensearch/api/_types/_common';
import { normalizeSizeLabel } from '@/utils';

import {
  FilterAttributeNameMapping,
  PRODUCT_FILTER_ATTRIBUTES,
} from '@/constants';

import { Bucket, RefinementListItem, SORT_ORDER } from '../types';
import { DefaultSortingFilter, OnSaleQueryFilter } from '../constants';

const initialAggsValue: Record<string, AggregationContainer> = {};

export const sanitizeQueryContainer = (container: QueryContainer): boolean => {
  const containerValues = Object.values(container || {});

  if (!containerValues.length) return false;

  if (containerValues && typeof containerValues[0] !== 'object') return true;

  return sanitizeQueryContainer(containerValues[0] as QueryContainer);
};

export const buildSmartFacetAggregations = (
  queryFiltersByFacets: Record<string, QueryContainer>,
) => {
  const facetFields = Object.keys(queryFiltersByFacets);

  return {
    facets: {
      global: {},
      aggs: facetFields.reduce((acc, facet) => {
        const otherFields = { ...queryFiltersByFacets };
        delete otherFields[facet];

        const isNumericFilter = !!(
          queryFiltersByFacets[facet]?.bool?.filter as QueryContainer
        )?.range;

        return {
          ...acc,
          ...(isNumericFilter
            ? {
                [`${facet}_max`]: {
                  max: {
                    field: facet,
                  },
                },
              }
            : {}),
          [facet]: {
            filter: {
              bool: {
                must: Object.values(otherFields).filter(sanitizeQueryContainer),
              },
            },
            aggs: {
              values: {
                terms: {
                  field: facet,
                  size: isNumericFilter ? 1 : 200,
                },
              },
            },
          },
        };
      }, initialAggsValue),
    },
  };
};

export const buildFacetQueries = (
  queryFiltersByFacets: Record<string, QueryContainer>,
) => ({
  bool: {
    must: Object.values(queryFiltersByFacets).filter(sanitizeQueryContainer),
  },
});

export const buildFacetQueryFilters = (facet: string, values: MatchQuery[]) =>
  facet === PRODUCT_FILTER_ATTRIBUTES._ON_SALE
    ? OnSaleQueryFilter
    : {
        bool: {
          should: values.map(value => ({
            match: {
              [facet]: value,
            },
          })),
        },
      };

export const buildQueryFilterFromNumericItem = (
  facet: string,
  { start, end }: { start?: number; end?: number },
) => ({
  bool: {
    filter: {
      range: {
        [facet]: {
          ...(start !== undefined ? { gte: start } : {}),
          ...(end !== undefined ? { lte: end } : {}),
        },
      },
    },
  },
});

export const transformBucketToRefinementItem = (
  { key, key_as_string, doc_count }: Bucket,
  attribute: string,
): RefinementListItem => {
  const label = key === 1 ? 'Active' : key.toString();
  const labelFormatted =
    attribute === 'customSize' ? normalizeSizeLabel(label) : label;
  return {
    label: labelFormatted,
    value: key_as_string ? JSON.parse(key_as_string) : key.toString(),
    count: doc_count,
    isRefined: false,
  };
};

export const getQueryFiltersArrayByFacets = (
  queryFiltersByFacets: Record<string, QueryContainer>,
): Record<string, MatchQuery[]> => {
  return Object.entries(queryFiltersByFacets).reduce((acc, [facet, query]) => {
    const matchQueries = (query.bool?.should as MatchQuery[]) || [];
    acc[facet] = matchQueries;
    return acc;
  }, {} as Record<string, MatchQuery[]>);
};

export const buildQueryFilterFromSearchParams = (
  searchParams: Record<string, string | string[] | boolean[]>,
): Record<string, QueryContainer> => {
  return Object.entries(searchParams).reduce((acc, [facet, values]) => {
    const isNumericParam = values.toString().includes(':');

    if (facet === 'onSale') {
      if ([values, values[0]].includes('true')) {
        acc[facet] = OnSaleQueryFilter;
      }
    } else if (isNumericParam) {
      const [start, end] = values
        .toString()
        .split(':')
        .map(value => (value ? Number(value) : undefined));
      acc[facet] = buildQueryFilterFromNumericItem(facet, { start, end });
    } else if (
      ![
        PRODUCT_FILTER_ATTRIBUTES._SORT,
        PRODUCT_FILTER_ATTRIBUTES._QUERY,
        PRODUCT_FILTER_ATTRIBUTES._PAGE,
      ].includes(facet as PRODUCT_FILTER_ATTRIBUTES)
    ) {
      acc[facet] = buildFacetQueryFilters(
        facet,
        Array.isArray(values)
          ? values
          : values === 'true'
          ? [true]
          : typeof values === 'string'
          ? values.split(',').map(value => value.trim())
          : values,
      );
    }

    return acc;
  }, {} as Record<string, QueryContainer>);
};

// Builds the search query filter for OpenSearch based on the provided query string
export const buildSearchQueryFilter = (
  query?: string,
  searchableAtributes: string[] = [
    'brand^12',
    'category^12',
    'menuTitle^5',
    'subtype.keyword^4',
    'effects.keyword^2',
    'classification.keyword^2',
    'description^1',
    'flavors.keyword^1',
  ],
): Record<string, QueryContainer> =>
  query
    ? {
        search: {
          bool: {
            should: [
              {
                multi_match: {
                  query,
                  fields: searchableAtributes,
                  fuzziness: 'AUTO',
                  type: 'most_fields',
                },
              },
              {
                multi_match: {
                  query,
                  type: 'phrase_prefix',
                  fields: searchableAtributes,
                  max_expansions: 10,
                },
              },
            ],
          },
        },
      }
    : {};

export const buildSortFilterFromSearchParams = (
  searchParams: Record<string, string | string[] | boolean[]>,
): Sort | undefined => {
  const sort = searchParams?.sort as string | undefined;
  // const query = searchParams?.query as string | undefined;

  if (!sort) {
    // if (!query) {
    //   return DefaultSortingFilter;
    // }
    return undefined;
  }

  // sort could be in the format "fieldAsc" or "fieldDesc"
  const sortField = sort.replace(/(Asc|Desc)$/, '');
  const mappedField = FilterAttributeNameMapping[sortField] || sortField;
  const sortOrder = sort.endsWith('Asc') ? SORT_ORDER._ASC : SORT_ORDER._DESC;

  if (!sortField || !sortOrder) return DefaultSortingFilter;

  return [{ [mappedField]: { order: sortOrder } }];
};

export const getFieldAndOrderFromSortFilter = (
  sortFilter: Sort | undefined,
  attributeMapping?: Record<string, string>,
): { field?: string; order?: SORT_ORDER } => {
  if (!sortFilter || !Array.isArray(sortFilter) || sortFilter.length === 0) {
    return {};
  }

  const sortItem = sortFilter[0];
  const key = Object.keys(sortItem)[0];
  const field = attributeMapping?.[key] ?? key;
  const order = (Object.values(sortItem)[0] as { order: SORT_ORDER })?.order;

  return { field, order };
};

const currencyFormat = (value: number) => {
  if (!value) {
    return 0;
  }

  const toFloat = value / 100;

  return toFloat === Math.floor(toFloat)
    ? Math.floor(toFloat)
    : toFloat.toFixed(2);
};

const pennyValue = process.env.NEXT_PUBLIC_DISCOUNT_MINIMUM_THRESHOLD_VALUE;
const minPriceValue = Number(currencyFormat(Number(pennyValue)));

export const getPriceFilter = ({
  includePromoItems,
  priceStart,
  priceEnd = 500000, // maximum price cap
}: {
  includePromoItems?: boolean;
  priceStart?: number;
  priceEnd?: number;
}): QueryContainer => ({
  bool: {
    filter: {
      range: {
        customMinPrice: {
          gte: priceStart ?? (includePromoItems ? 0.01 : minPriceValue),
          lte: priceEnd,
        },
      },
    },
  },
});
